import {Component, Input, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {REGIONS} from '@constants/administration/gateway/iot-core-regions';
import {GATEWAY_TYPE} from '@constants/gateway/gateway-type';
import {BIGQUERY_VALID_NAME_REGEX, GATEWAY_VALID_ID_REGEX} from '@constants/reg-exp';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {GatewayIot} from '@models/adminstration/gateway/gateway-configuration/gateway-iot';
import {ElasticSearchResult} from '@models/elastic-search/response/elastic-search-result';
import {Machine} from '@models/machine/machine';
import {Organization} from '@models/organization/organization';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';
import {GatewayService} from '@services/administration/gateway/gateway.service';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {NotificationService} from '@services/notification/notification.service';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';

@Component({
    selector: 'scitis-admin-gateway-edit',
    templateUrl: './gateway-edit.component.html',
    styleUrls: ['./gateway-edit.component.scss'],
    standalone: false
})
export class GatewayEditComponent implements OnInit {

  @Input()
  gateway: Gateway = new Gateway();

  @Input()
  machines: Machine[] = new Array<Machine>();

  @Input()
  lightCloudplugs: Gateway[] = new Array<Gateway>();

  @Input()
  rootOrganizationId: number | null = null;

  sourceCloudplugLight: Gateway | null = null;
  gatewayForm: UntypedFormGroup = new UntypedFormGroup({});
  possibleGatewayTypes: string[] = Object.values(GATEWAY_TYPE);
  possibleOrganizations: Organization[] = new Array<Organization>();
  possibleMachines: Machine[] = new Array<Machine>();
  selectedMachine: Machine = new Machine();
  selectedOrganization: Organization = new Organization();

  submitted: boolean = false;
  copySettings: boolean = false;
  loadRegistryIds: boolean = false;
  isLoadingOrganizations: boolean = false;
  isLoadingMachines: boolean = false;
  isMachinesEditable: boolean = false;

  defaultRegion = REGIONS.EUROPE;
  iotCoreRegistryIds: string[] = new Array<string>();
  public checkingForSerialNumberAlreadyUsed: boolean = false;
  private COMPONENT_NAME: string = 'GatewayEditComponent';

  constructor(private activeModal: NgbActiveModal,
              private elasticSearchService: ElasticSearchService,
              private sortingService: SortingService,
              private translateService: TranslateService,
              private notificationService: NotificationService,
              private gatewayService: GatewayService) {
  }

  get isDisabledSaveBtn(): boolean {
    return this.submitted ? this.gatewayForm.invalid : this.submitted && this.gatewayForm.invalid;
  };

  // convenience getter for easy access to form fields
  get f() {
    return this.gatewayForm.controls;
  }

  ngOnInit(): void {
    this.setOrganizations();
    this.generateGatewayForm(this.gateway);
    this.setDisabledStatusForInput(this.isMachinesEditable, 'machine');
    this.setDisabledStatusForInput(this.copySettings, 'sourceCloudplugLight');
    this.listRegistryIds();
  }

  onClose() {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onSave() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.gatewayForm.invalid) {
      return;
    }
    this.updateGatewayProperties();
    this.activeModal.close({
      sourceId: !this.sourceCloudplugLight || !this.isLightCloudplug() ? -1 : this.sourceCloudplugLight.id,
      gateway: this.gateway
    });
  }

  onCancel() {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onGatewayTypeChange(type: GATEWAY_TYPE) {
    this.sourceCloudplugLight = null;
    this.gateway.iotCore = new GatewayIot();
    this.gateway.cloudplugType = type;
    this.copySettings = false;
    this.changeValidation(type);
  }

  isLightCloudplug() {
    return this.gateway.cloudplugType === GATEWAY_TYPE.CPLIGHT;
  }

  isGenericCloudplug() {
    return this.gateway.cloudplugType === GATEWAY_TYPE.GENERIC;
  }

  isArgosGateway() {
    return this.gateway.cloudplugType === GATEWAY_TYPE.ARGOS_GATEWAY;
  }

  onCopySettingsChange() {
    this.copySettings = !this.copySettings;
    this.setDisabledStatusForInput(this.copySettings, 'sourceCloudplugLight');
    if (!this.copySettings) {
      this.sourceCloudplugLight = null;
    }
  }

  onSourceCloudplugLightChange(sourceCPLight: Gateway) {
    if (!sourceCPLight) {
      this.sourceCloudplugLight = null;
      return;
    }
    this.f.name.setValue(sourceCPLight.name);
    this.f.gatewayType.setValue(sourceCPLight.cloudplugType);
    this.f.tenant.setValue(sourceCPLight.tenantName);
    this.f.machine.setValue(sourceCPLight.machineName);
    this.loadMachineBasedOnSelectedTenant(sourceCPLight.tenantId);
    this.sourceCloudplugLight = sourceCPLight;
    this.selectedMachine = this.getMachine(sourceCPLight.machineId);
    this.selectedOrganization = this.getOrganization(sourceCPLight.tenantId);

    if (!sourceCPLight.iotCore) {
      this.f.region.setValue(this.defaultRegion);
    } else {
      this.f.region.setValue(sourceCPLight.iotCore.cloudRegion);
      this.defaultRegion = sourceCPLight.iotCore.cloudRegion;
      this.listRegistryIds();
    }
  }

  onChangeOrganization(organization: any) {
    this.isMachinesEditable = false;
    this.setDisabledStatusForInput(this.isMachinesEditable, 'machine');
    this.isLoadingMachines = true;
    this.selectedOrganization = organization;
    this.loadMachineBasedOnSelectedTenant(organization.id);
  }

  onChangeMachine(machine: any) {
    this.selectedMachine = machine;
  }

  onSerialNumberChange() {
    const serial: string = this.f.serial.value;
    if (!serial || serial.length === 0 || serial.trim().length === 0 || this.f.serial.errors) {
      return;
    }
    this.gatewaySerialNumberAlreadyExists(serial);
  }

  private generateGatewayForm(gateway: Gateway) {
    this.gatewayForm = new UntypedFormGroup({
      gatewayType: new UntypedFormControl(gateway.cloudplugType, [Validators.required]),
      name: new UntypedFormControl(gateway.name, [Validators.required, Validators.pattern(BIGQUERY_VALID_NAME_REGEX)]),
      tenant: new UntypedFormControl(gateway.tenantName, [Validators.required]),
      serial: new UntypedFormControl(gateway.id, [Validators.required, Validators.minLength(5), Validators.pattern(GATEWAY_VALID_ID_REGEX)]),
      machine: new UntypedFormControl(gateway.machineName, [Validators.required]),
      sourceCloudplugLight: new UntypedFormControl(),
      region: new UntypedFormControl(this.defaultRegion),
      registry: new UntypedFormControl(),
    });
  }

  private setOrganizations() {
    this.isLoadingOrganizations = true;
    this.elasticSearchService.listAllOrganizations(this.rootOrganizationId).subscribe(
        {
          next: (success) => {
            if (success?.result?.data && success.result.data.length > 0) {
              const result: ElasticSearchResult = success.result;
              this.possibleOrganizations = [...result.data];
              this.possibleOrganizations.sort((a, b) => this.sortingService.sortByName(a, b));
            }
            this.isLoadingOrganizations = false;
          }, error: error => {
            const message = this.translateService.instant('Error getting organizations list.');
            this.notificationService.error(message, error, this.COMPONENT_NAME, true);
            this.isLoadingOrganizations = false;
          }
        });
  }

  private loadMachineBasedOnSelectedTenant(organizationId: number) {
    this.isLoadingMachines = true;
    this.possibleMachines = [];
    for (const machine of this.machines) {
      if (machine.parentId === organizationId) {
        this.possibleMachines = [...this.possibleMachines, machine];
      }
    }
    this.possibleMachines.sort((a, b) => this.sortingService.sortByName(a, b));
    this.isLoadingMachines = false;
    this.isMachinesEditable = true;
    this.setDisabledStatusForInput(this.isMachinesEditable, 'machine');
  }

  private setDisabledStatusForInput(editable: boolean, inputId: string) {
    if (editable) {
      this.gatewayForm.get(inputId)?.enable();
    } else {
      this.gatewayForm.get(inputId)?.disable();
    }
  }

  private updateGatewayProperties() {
    this.gateway.cloudplugType = this.f.gatewayType.value;
    this.gateway.name = this.f.name.value;
    this.gateway.tenantName = this.f.tenant.value;
    this.gateway.id = this.f.serial.value;
    this.gateway.machineName = this.f.machine.value;
    this.gateway.iotCore = {
      cloudRegion: this.f.region.value,
      registryId: this.f.registry.value
    };
    if (this.selectedMachine.id !== undefined) {
      this.gateway.machineId = this.selectedMachine.id;
    }
    this.gateway.tenantId = this.selectedOrganization.id;
  }

  private listRegistryIds() {
    this.gatewayService.getIotRegistryIds().subscribe({
      next: registryIds => {
        this.iotCoreRegistryIds = registryIds;
        this.loadRegistryIds = false;
      }, error: error => {
        console.log(error);
        this.loadRegistryIds = false;
      }
    });
  }

  private getMachine(machineId: number) {
    let machine: Machine = new Machine();
    for (const m of this.machines) {
      if (m.id === machineId) {
        machine = m;
        break;
      }
    }
    return machine;
  }

  private getOrganization(organizationId: number) {
    let organization: Organization = new Organization();
    for (const org of this.possibleOrganizations) {
      if (org.id === organizationId) {
        organization = org;
        break;
      }
    }
    return organization;
  }

  private changeValidation(type: GATEWAY_TYPE) {
    if (type === GATEWAY_TYPE.CPLIGHT || type === GATEWAY_TYPE.GENERIC) {
      this.f.region.setValidators(Validators.required);
      this.f.registry.setValidators(Validators.required);
    } else {
      this.f.region.clearValidators();
      this.f.registry.clearValidators();
    }
  }

  private gatewaySerialNumberAlreadyExists(serialNumber: string) {
    this.checkingForSerialNumberAlreadyUsed = true;

    this.elasticSearchService.isGatewayPropertyAlreadyUsed(serialNumber, true).subscribe({
      next: (response) => {
        if (response?.result?.totalHits > 0) {
          this.f.serial.setErrors({used: true});
        } else {
          this.f.serial.setErrors(null);
        }
        this.checkingForSerialNumberAlreadyUsed = false;

      }, error: (error) => {
        const message = this.translateService.instant('Cannot verify for gateway serial number usage');
        this.notificationService.error(message, error, 'gateway', true);
        this.f.serial.setErrors({used: false});
        this.checkingForSerialNumberAlreadyUsed = false;
      }
    });
  }


}
